import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Shield, UserCheck, Star, Heart, Award, ArrowRight, User } from "lucide-react";
import { motion } from "framer-motion";
import useCounter from "../hooks/useCounter";
import gsap from "gsap";

const ABOUT_IMG = "/assets/atf_command_center.png";

const FadeUp = ({ children, delay = 0, className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: delay / 1000, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StatItem = ({ value, suffix, label, displayText, startCounting }) => {
  const count = useCounter(value || 0, 1800, startCounting);
  return (
    <div className="py-10 px-8 text-center border-white/10 relative group">
      <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-500 rounded-2xl mx-2" />
      <div className="relative z-10">
        <div className="font-display font-medium text-4xl md:text-5xl text-white mb-3 tracking-tight drop-shadow-lg">
          {displayText ? displayText : `${count.toLocaleString()}${suffix}`}
        </div>
        <div className="font-sans text-[10px] md:text-[11px] font-bold tracking-[0.25em] uppercase text-white/50">
          {label}
        </div>
      </div>
    </div>
  );
};

const values = [
  { icon: Shield, title: "Professional Discipline", desc: "We follow defined processes and ensure every task is executed properly across all levels of operation." },
  { icon: UserCheck, title: "Accountability", desc: "We take full responsibility for the performance and conduct of every deployed team member." },
  { icon: Star, title: "Transparency", desc: "Clients have clear visibility into our operations, reporting protocols, and statutory compliance." },
  { icon: Target, title: "Preparedness", desc: "We maintain constant readiness to handle routine operations as well as unexpected tactical situations." },
  { icon: Heart, title: "Respect for People", desc: "We value both our workforce and our clients, ensuring professional and respectful engagement at all levels." },
];

const team = [
  {
    name: "Mehul Barot",
    role: "Director",
    image: "/assets/team/mehul_barot.png",
    desc: "Strategic leader focused on expanding ATF's footprint while maintaining the highest standards of security and manpower excellence.",
    type: "executive"
  },
  {
    name: "Nagji Desai",
    role: "Director",
    image: "/assets/team/nagji_desai.png",
    desc: "Operational specialist dedicated to client success through disciplined service delivery and robust process management.",
    type: "executive"
  },
  {
    name: "Abhaykumar Mishra",
    role: "RTD Flying Officer - CFO",
    image: "/assets/team/abhaykumar_mishra.png",
    desc: "Brings military precision to ATF's financial operations, ensuring transparency and absolute statutory compliance.",
    type: "executive"
  },
  {
    name: "Sanjay Singh",
    role: "Wg Cdr (Retd.) - Chief Mentor",
    image: "/assets/team/sanjay_singh.png",
    desc: "Distinguished veteran providing strategic vision and mentoring the leadership team in operational discipline.",
    type: "operational"
  },
  {
    name: "Ramesh Thakor",
    role: "Talent & Acquisition Officer",
    image: "/assets/team/ramesh_thakor.png",
    desc: "Leading the recruitment and vetting process to ensure ATF deploys only the most reliable and capable personnel.",
    type: "operational"
  },
  {
    name: "Tejal Shashtri",
    role: "Customer Relationship Officer",
    image: "/assets/team/tejal_shashtri.png",
    desc: "Bridging the gap between client expectations and operational delivery through proactive relationship management.",
    type: "operational"
  },
  {
    name: "Sneh Jain",
    role: "Finance Manager",
    image: null,
    desc: "Overseeing fiscal health and financial reporting with a focus on accuracy and operational efficiency.",
    type: "operational"
  },
  {
    name: "Hitesh",
    role: "Training Officer",
    image: null,
    desc: "Directing the training programs that define ATF's reputation for disciplined and professional security personnel.",
    type: "operational"
  },
];

const certifications = [
  { 
    title: "PSARA Licensed", 
    desc: "Fully authorized by state authorities to provide professional security services." 
  },
  { 
    title: "Statutory Compliant", 
    desc: "100% adherence to ESI, PF, and labor norms for workforce stability." 
  },
  { 
    title: "Personnel Verified", 
    desc: "Thorough police background checks and identity verification for every operative." 
  },
  { 
    title: "24/7 Monitoring", 
    desc: "Continuous operational oversight from our dedicated Monitoring Hub." 
  },
];

const ValueCard = ({ value, delay }) => {
  const cardRef = useRef(null);
  const bgRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const iconRef = useRef(null);
  const iconBoxRef = useRef(null);
  const tweenRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      tweenRef.current = gsap.timeline({ paused: true })
        .to(bgRef.current, { 
          clipPath: "circle(150% at 0% 0%)", 
          duration: 1.0, 
          ease: "power2.inOut" 
        }, 0)
        .to(titleRef.current, { color: "#ffffff", duration: 0.8, ease: "power2.out" }, 0.2)
        .to(descRef.current, { color: "rgba(255, 255, 255, 0.8)", duration: 0.8, ease: "power2.out" }, 0.2)
        .to(iconBoxRef.current, { backgroundColor: "rgba(255, 255, 255, 0.2)", duration: 0.8, ease: "power2.out" }, 0)
        .to(iconRef.current, { color: "#ffffff", duration: 0.8, ease: "power2.out" }, 0);
    }, cardRef);

    return () => ctx.revert();
  }, []);

  const handleMouseEnter = () => tweenRef.current?.play();
  const handleMouseLeave = () => tweenRef.current?.reverse();
  
  const Icon = value.icon;

  return (
    <FadeUp delay={delay} className="h-full">
      <div 
        ref={cardRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="group relative bg-white rounded-3xl p-10 border border-[#141414]/5 transition-transform duration-700 overflow-hidden h-full flex flex-col hover:-translate-y-2 cursor-pointer shadow-[0_40px_80px_rgba(0,0,0,0.02)]"
      >
        <div 
          ref={bgRef} 
          className="absolute inset-0 bg-[#835F21] z-0 pointer-events-none" 
          style={{ clipPath: "circle(0% at 0% 0%)" }}
        />
        <div className="relative z-10 flex flex-col h-full pointer-events-none">
          <div ref={iconBoxRef} className="w-14 h-14 rounded-2xl bg-[#835F21]/10 flex items-center justify-center mb-8">
            <Icon ref={iconRef} size={24} className="text-[#835F21] stroke-[1.5]" />
          </div>
          <h3 ref={titleRef} className="font-display font-medium text-xl text-[#141414] mb-4 tracking-tight">{value.title}</h3>
          <p ref={descRef} className="font-sans text-[14px] text-[#141414]/60 leading-relaxed font-light flex-grow">{value.desc}</p>
        </div>
      </div>
    </FadeUp>
  );
};

const MissionVisionCard = ({ icon: Icon, title, desc, delay = 0, defaultIconBg = "bg-[#835F21]/10", defaultIconColor = "text-[#835F21]" }) => {
  const cardRef = useRef(null);
  const bgRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const iconRef = useRef(null);
  const iconBoxRef = useRef(null);
  const tweenRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      tweenRef.current = gsap.timeline({ paused: true })
        .to(bgRef.current, { 
          clipPath: "circle(150% at 0% 100%)", 
          duration: 1.0, 
          ease: "power2.inOut" 
        }, 0)
        .to(titleRef.current, { color: "#ffffff", duration: 0.8, ease: "power2.out" }, 0.2)
        .to(descRef.current, { color: "rgba(255, 255, 255, 0.8)", duration: 0.8, ease: "power2.out" }, 0.2)
        .to(iconBoxRef.current, { backgroundColor: "rgba(255, 255, 255, 0.2)", duration: 0.8, ease: "power2.out" }, 0)
        .to(iconRef.current, { color: "#ffffff", duration: 0.8, ease: "power2.out" }, 0);
    }, cardRef);

    return () => ctx.revert();
  }, []);

  const handleMouseEnter = () => tweenRef.current?.play();
  const handleMouseLeave = () => tweenRef.current?.reverse();

  return (
    <FadeUp delay={delay} className="h-full">
      <div 
        ref={cardRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="group relative bg-white border border-[#141414]/5 p-12 rounded-[2.5rem] shadow-[0_40px_80px_rgba(0,0,0,0.02)] transition-transform duration-700 overflow-hidden h-full flex flex-col hover:-translate-y-2 cursor-pointer"
      >
        <div 
          ref={bgRef} 
          className="absolute inset-0 bg-[#835F21] z-0 pointer-events-none" 
          style={{ clipPath: "circle(0% at 0% 100%)" }}
        />
        <div className="absolute top-0 right-0 w-[150px] h-[150px] bg-[#835F21]/5 rounded-full blur-3xl pointer-events-none z-0" />
        
        <div className="relative z-10 flex flex-col h-full pointer-events-none">
          <div ref={iconBoxRef} className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-10 ${defaultIconBg}`}>
            <Icon ref={iconRef} size={32} className={`${defaultIconColor} stroke-[1.5]`} />
          </div>
          <h3 ref={titleRef} className="font-display font-medium text-3xl text-[#141414] mb-6 tracking-tight">{title}</h3>
          <p ref={descRef} className="font-sans text-[15px] text-[#141414]/60 leading-relaxed font-light flex-grow">{desc}</p>
        </div>
      </div>
    </FadeUp>
  );
};


export default function About() {




  return (
    <main className="bg-[#F9F8F5] overflow-hidden font-sans text-[#141414]" data-testid="about-page">
      {/* ── 01. Institutional Hero ── */}
      <section className="bg-[#F9F8F5] h-[95vh] flex items-center relative overflow-hidden" data-testid="about-hero">
        {/* Background Large Watermark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none whitespace-nowrap z-0">
          <h2 className="font-display font-bold text-[30vw] tracking-tighter leading-none text-[#141414]">
            ABOUT US
          </h2>
        </div>

        <div className="absolute inset-0 z-0">
          {/* Decorative Background Elements */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#835F21]/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4" />
          
          <div 
            className="absolute inset-y-0 left-0 w-full lg:w-[50%] bg-[#F9F8F5]/90 backdrop-blur-md z-10 pointer-events-none" 
            style={{ clipPath: "polygon(0 0, 85% 0, 100% 100%, 0 100%)" }}
          />
          
          <div className="absolute inset-0 z-0">
            <img src="/assets/protection.png" alt="Astraum Task Force Identity" className="w-full h-full object-cover object-[85%_center] opacity-80" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#F9F8F5] via-[#F9F8F5]/30 to-transparent z-[5]" />
        </div>
        
        <div className="relative z-20 w-full lg:w-[50%] px-6 md:px-12 lg:pl-24 lg:pr-12">
          <FadeUp>
            <div className="inline-flex items-center gap-3 mb-8 px-5 py-2.5 bg-white shadow-xl rounded-full border border-[#835F21]/10">
              <span className="w-2 h-2 rounded-full bg-[#835F21] animate-pulse" />
              <span className="font-sans text-[11px] font-bold tracking-[0.25em] text-[#141414] uppercase">Our Institutional Identity</span>
            </div>
            
            <h1 className="font-display font-medium text-[#141414] leading-[0.95] mb-10 tracking-tighter"
              style={{ fontSize: "clamp(56px, 8vw, 110px)" }}
            >
              The DNA of <br />
              <span className="text-[#835F21] italic font-light">ATF Security.</span>
            </h1>
            
            <div className="w-20 h-1 bg-[#835F21] mb-10" />
            
            <p className="font-sans text-xl md:text-3xl text-[#141414]/70 leading-relaxed font-light max-w-2xl">
              Building a legacy of <span className="text-[#141414] font-medium">disciplined protection</span> and professional manpower solutions across the Indian landscape.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ── 02. The ATF Foundation ── */}
      <section className="bg-white py-32 md:py-48" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-start">
            <FadeUp>
              <span className="section-label block mb-6 text-[#835F21]">Who We Are</span>
              <h2 className="font-display font-medium text-[#141414] leading-[1.1] mb-12 tracking-tight" style={{ fontSize: "clamp(44px, 5vw, 64px)" }}>
                Focused on Practical <br />
                <span className="text-[#835F21] italic font-light">Security Solutions.</span>
              </h2>
              <div className="space-y-10">
                <p className="font-sans text-[17px] text-[#141414]/70 leading-relaxed font-light">
                  Founded by experienced professionals, ATF was built with a clear goal — to provide disciplined security services and dependable manpower, not just basic staffing. Every deployment we handle is treated as a responsibility toward the client’s operations and safety.
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center gap-3">
                    <Shield size={18} className="text-[#835F21]" />
                    <span className="font-sans font-medium text-[#141414]">Proper Training</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield size={18} className="text-[#835F21]" />
                    <span className="font-sans font-medium text-[#141414]">Clear Processes</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield size={18} className="text-[#835F21]" />
                    <span className="font-sans font-medium text-[#141414]">Continuous Monitoring</span>
                  </div>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={200} className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-[#141414]/10 group">
                <img 
                  src={ABOUT_IMG} 
                  alt="ATF Operations Center" 
                  className="w-full aspect-[4/5] object-cover transition-transform duration-1000 group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-10 left-10 right-10">
                  <div className="backdrop-blur-md bg-white/5 border border-white/10 p-8 rounded-2xl text-white">
                    <p className="font-display font-light text-2xl mb-4 leading-tight">
                      "At Astraum Task Force, we focus on doing the basics right—consistently and professionally."
                    </p>
                    <div className="w-12 h-[1px] bg-[#835F21]" />
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>



      {/* ── 04. Mission & Vision ── */}
      <section className="bg-[#F0EBE6] py-32 md:py-48 relative overflow-hidden" data-testid="about-mission-vision">
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-16 xl:gap-24 items-center">
            <div className="xl:col-span-5">
              <FadeUp>
                <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Our Approach</span>
                <h2 className="font-display font-medium text-[#141414] leading-[1.05] mb-8 tracking-tight" style={{ fontSize: "clamp(44px, 5vw, 72px)" }}>
                  Simple. <br /> 
                  <span className="text-[#835F21] italic font-light">Structured. Reliable.</span>
                </h2>
                <p className="font-sans text-lg text-[#141414]/60 leading-relaxed font-light">
                  We help businesses maintain safe environments, smooth operations, and consistent on-ground control through structured deployment and supervision.
                </p>
              </FadeUp>
            </div>

            <div className="xl:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-8">
              <MissionVisionCard 
                icon={Target}
                title="Our Mission"
                desc="To provide trained, verified, and well-managed manpower and security services that help businesses operate safely and efficiently."
                defaultIconBg="bg-[#835F21] shadow-[0_0_30px_rgba(131,95,33,0.3)]"
                defaultIconColor="text-white"
                delay={200}
              />
              <MissionVisionCard 
                icon={Eye}
                title="Our Vision"
                desc="To become a trusted name in security and facility services across India, known for reliability, discipline, and consistent service delivery."
                defaultIconBg="bg-[#835F21]/10"
                defaultIconColor="text-[#835F21]"
                delay={400}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── 05. Leadership Team ── */}
      <section className="bg-white py-32 md:py-48 relative overflow-hidden" data-testid="about-team">
        {/* Background Large Text */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-[0.02] select-none whitespace-nowrap">
          <h2 className="font-display font-bold text-[20vw] tracking-tighter leading-none text-[#141414]">
            LEADERSHIP
          </h2>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-24 gap-12">
            <FadeUp className="max-w-3xl">
              <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Our Leadership Team</span>
              <h2 className="font-display font-medium text-[#141414] leading-[1.05] tracking-tight" style={{ fontSize: "clamp(44px, 5vw, 64px)" }}>
                The People Behind <br /> <span className="italic font-light text-[#835F21]">Our Commitment.</span>
              </h2>
            </FadeUp>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {team.map((member, i) => (
              <FadeUp key={i} delay={i * 100}>
                <div className="group relative bg-[#F9F8F5] rounded-[2rem] overflow-hidden border border-[#141414]/5 transition-all duration-700 hover:shadow-2xl hover:-translate-y-2 h-full flex flex-col">
                  {/* Image Container - Reduced Height Aspect Ratio */}
                  <div className="aspect-[3/2] overflow-hidden bg-[#835F21]/5 relative">
                    {member.image ? (
                      <img 
                        src={member.image} 
                        alt={member.name} 
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User size={64} className="text-[#835F21]/20" strokeWidth={1} />
                      </div>
                    )}
                    {/* Subtle Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#141414]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  
                  {/* Content Container */}
                  <div className="p-8 flex flex-col flex-grow">
                    <h3 className="font-display font-medium text-2xl text-[#141414] mb-1 tracking-tight">{member.name}</h3>
                    <p className="font-sans text-[10px] font-bold text-[#835F21] uppercase tracking-[0.2em] mb-4">
                      {member.role}
                    </p>
                    <p className="font-sans text-[14px] text-[#141414]/60 leading-relaxed font-light">
                      {member.desc}
                    </p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── 05. Core Values ── */}
      <section className="bg-[#F9F8F5] py-32 md:py-48" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-24 gap-12">
            <FadeUp className="max-w-3xl">
              <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Our Values</span>
              <h2 className="font-display font-medium text-[#141414] leading-[1.05] tracking-tight" style={{ fontSize: "clamp(44px, 5vw, 64px)" }}>
                The Axioms That <br /> <span className="italic font-light">Drive Our Service</span>
              </h2>
            </FadeUp>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
            {values.map((v, i) => (
              <ValueCard key={i} value={v} delay={i * 100} />
            ))}
          </div>
        </div>
      </section>



      {/* ── 06. Compliance Grid ── */}
      <section className="bg-[#141414] py-32 md:py-48 overflow-hidden relative" data-testid="about-certifications">
        <div className="absolute inset-0 bg-[#835F21]/5 mix-blend-overlay pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-24 max-w-3xl mx-auto">
            <FadeUp>
              <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Fully Compliant Operations</span>
              <h2 className="font-display font-medium text-white tracking-tight" style={{ fontSize: "clamp(40px, 5vw, 64px)" }}>
                Compliance & <br /> <span className="italic font-light">Certifications</span>
              </h2>
            </FadeUp>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((c, i) => (
              <FadeUp key={i} delay={i * 100}>
                <div className="bg-white/5 border border-white/10 rounded-3xl p-10 text-center hover:border-[#835F21]/50 hover:bg-white/10 transition-all duration-300 group">
                  <div className="w-16 h-16 rounded-full bg-black/30 border border-white/10 flex items-center justify-center mx-auto mb-8 group-hover:bg-[#835F21] group-hover:border-[#835F21] transition-all duration-500">
                    <Award size={28} className="text-[#835F21] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display font-medium text-xl text-white mb-4 tracking-tight">{c.title}</h3>
                  <p className="font-sans text-[11px] font-bold text-white/40 uppercase tracking-[0.15em] leading-relaxed">{c.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── 07. Final Briefing ── */}
      <section className="bg-[#835F21] py-24 md:py-32" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <FadeUp className="text-center lg:text-left max-w-3xl">
              <h2 className="font-display font-medium text-white text-4xl lg:text-6xl tracking-tight mb-4">
                A Reliable Partner <br /> <span className="italic font-light">For Your Operations.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={200}>
              <Link to="/contact" className="btn-primary bg-white text-[#141414] border-none hover:bg-[#F9F8F5] hover:text-black shadow-[0_0_30px_rgba(255,255,255,0.15)] h-16 px-12 group tracking-[0.2em]">
                Schedule Consultation
                <ArrowRight size={18} className="ml-3 group-hover:translate-x-1 transition-transform" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
